/**
 * Voice Mode State Management
 *
 * Pure functions for computing voice mode state and UI text.
 * This centralizes all voice mode logic for testability and predictability.
 */

// Input interfaces - all the raw state we receive
export interface VoiceModeInputs {
  // STT Service states
  isListening: boolean
  isHearingUser: boolean
  isTranslatingSTT: boolean
  isTranslatingTTS: boolean

  // Component states
  isWaitingForResponseFromAi: boolean
  isMuted: boolean
  isAiSpeaking: boolean
}

// Translation interface - all the text we need
export interface VoiceModeTranslations {
  processingHeader: string
  processingSubheader: string
  headerAvailable: string
  subheaderAvailable: string
  headerListening: string
  headerSharingWisdom: string
  mutedHeader: string
  mutedSubheader: string
}

// Output interface - computed state and UI text
export interface VoiceModeState {
  // Computed boolean states
  userIsTalking: boolean
  processingUserResponse: boolean
  availableForInput: boolean

  // UI states
  currentHeaderText: string
  currentSubheaderText: string | null

  // Mode for debugging/testing
  mode: 'listening' | 'aiSpeaking' | 'available' | 'processing' | 'muted' | 'mutedIdle'
}

/**
 * Main state computation function
 * Pure function - same inputs always produce same outputs
 */
export function computeVoiceModeState (
  inputs: VoiceModeInputs,
  translations: VoiceModeTranslations,
): VoiceModeState {
  // Step 1: Compute derived boolean states
  const userIsTalking = inputs.isHearingUser || false
  const processingUserResponse = inputs.isTranslatingSTT
    || inputs.isWaitingForResponseFromAi
    || inputs.isTranslatingTTS
  const availableForInput = inputs.isListening
    && !processingUserResponse
    && !inputs.isMuted

  // Step 2: Determine mode (for debugging and testing)
  let mode: VoiceModeState['mode']
  if (inputs.isMuted) {
    // If muted and not processing anything and AI not speaking, show muted idle state
    if (!processingUserResponse && !inputs.isAiSpeaking) {
      mode = 'mutedIdle'
    } else {
      mode = 'muted'
    }
  } else if (userIsTalking) {
    mode = 'listening'
  } else if (inputs.isAiSpeaking && !userIsTalking) {
    mode = 'aiSpeaking'
  } else if (availableForInput) {
    mode = 'available'
  } else {
    mode = 'processing'
  }

  // Step 3: Compute UI text based on priority
  const { currentHeaderText, currentSubheaderText } = computeUIText(
    {
      userIsTalking,
      isAiSpeaking: inputs.isAiSpeaking,
      availableForInput,
      processingUserResponse,
      isMuted: inputs.isMuted,
    },
    translations,
  )

  return {
    userIsTalking,
    processingUserResponse,
    availableForInput,
    currentHeaderText,
    currentSubheaderText,
    mode,
  }
}

/**
 * UI text computation - separated for easier testing
 */
function computeUIText (
  states: {
    userIsTalking: boolean
    isAiSpeaking: boolean
    availableForInput: boolean
    processingUserResponse: boolean
    isMuted: boolean
  },
  translations: VoiceModeTranslations,
): { currentHeaderText: string; currentSubheaderText: string | null } {
  // Priority order for header text

  // Check for muted idle state first (highest priority for muted state)
  if (states.isMuted && !states.processingUserResponse && !states.isAiSpeaking) {
    return {
      currentHeaderText: translations.mutedHeader,
      currentSubheaderText: translations.mutedSubheader,
    }
  }

  if (states.userIsTalking) {
    return {
      currentHeaderText: translations.headerListening,
      currentSubheaderText: null, // No subheader when user is talking
    }
  }

  if (states.isAiSpeaking && !states.userIsTalking) {
    return {
      currentHeaderText: translations.headerSharingWisdom,
      currentSubheaderText: translations.processingSubheader, // Show "jump in" text
    }
  }

  if (states.availableForInput) {
    return {
      currentHeaderText: translations.headerAvailable,
      currentSubheaderText: translations.subheaderAvailable,
    }
  }

  if (states.processingUserResponse) {
    return {
      currentHeaderText: translations.processingHeader,
      currentSubheaderText: translations.processingSubheader,
    }
  }

  // Default fallback
  return {
    currentHeaderText: translations.processingHeader,
    currentSubheaderText: translations.processingSubheader,
  }
}

/**
 * Helper function to create VoiceModeInputs from component state
 * This makes it easier to call from the component
 */
export function createVoiceModeInputs (componentState: {
  isListening: boolean
  isWaitingForResponseFromAi: boolean
  isMuted: boolean
  isAiSpeaking: boolean
  isTranslatingSTT: boolean
  userIsTalking: boolean
  isTranslatingTTS: boolean
}): VoiceModeInputs {
  return {
    isListening: componentState.isListening,
    isHearingUser: componentState.userIsTalking, // Map userIsTalking to isHearingUser
    isTranslatingSTT: componentState.isTranslatingSTT,
    isTranslatingTTS: componentState.isTranslatingTTS,
    isWaitingForResponseFromAi: componentState.isWaitingForResponseFromAi,
    isMuted: componentState.isMuted,
    isAiSpeaking: componentState.isAiSpeaking,
  }
}
